import React, { useState, useEffect } from 'react';
import { Member, ProjectMember } from '../types/api';
import { AllocationMember } from '../types/allocation';
import { MemberService } from '../services';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddMembers: (members: AllocationMember[]) => void;
  existingMemberIds: number[];
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  onClose,
  onAddMembers,
  existingMemberIds,
}) => {
  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which week's allocation data the list came from - not always the current week
  const [allocationWeek, setAllocationWeek] = useState<{ year?: number; week?: number }>({});

  // Fetch all members when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchMembers();
    }
  }, [isOpen]);

  const fetchMembers = () => {
    setLoading(true);
    setError(null);

    // Every allocatable member. Utilisation for the week rides along where it
    // exists; it does not decide who is listed.
    MemberService.getAvailableMembers()
      .then(({ members, actualYear, actualWeek }) => {
        setAllMembers(members);
        setAllocationWeek({ year: actualYear, week: actualWeek });
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load members. Please try again.');
        console.error('Error fetching available members:', err);
        setLoading(false);
      });
  };

  // Filter members based on search term and exclude existing members
  const filteredMembers = allMembers.filter(member => {
    const fullName = `${member.firstname} ${member.lastname}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         member.login.toLowerCase().includes(searchTerm.toLowerCase());
    const notAlreadyAdded = !existingMemberIds.includes(member.id);
    return matchesSearch && notAlreadyAdded;
  });

  // Handle member selection
  const handleMemberSelection = (member: Member) => {
    setSelectedMembers(prev => {
      const isSelected = prev.some(m => m.id === member.id);
      if (isSelected) {
        return prev.filter(m => m.id !== member.id);
      } else {
        return [...prev, member];
      }
    });
  };

  // Handle select all filtered members
  const handleSelectAll = () => {
    setSelectedMembers(filteredMembers);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedMembers([]);
  };

  // Handle adding selected members
  const handleAddMembers = () => {
    const allocationMembers: AllocationMember[] = selectedMembers.map(member => ({
      id: member.id,
      member: member,
      selected: false,
      removed: false,
      monthlyAllocations: {},
    }));

    onAddMembers(allocationMembers);
    setSelectedMembers([]);
    setSearchTerm('');
    onClose();
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedMembers([]);
    setSearchTerm('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        {/* Modal Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 id="modal-title" className="text-lg font-medium text-gray-900">Add Team Members</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Team members
              {allocationWeek.year && allocationWeek.week
                ? ` · allocation data for ${allocationWeek.year} week ${allocationWeek.week}`
                : ''}
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded p-1 transition-all"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p id="modal-description" className="sr-only">
          Search and select team members to add to the project allocation
        </p>

        {/* Search Bar */}
        <div className="mb-4">
          <label htmlFor="member-search" className="sr-only">
            Search members
          </label>
          <div className="relative">
            <input
              id="member-search"
              type="text"
              placeholder="Search members by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search members by name or email"
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Selection Controls */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            {selectedMembers.length} member(s) selected
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleSelectAll}
              aria-label="Select all filtered members"
              className="text-sm text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-2 py-1 transition-all"
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              aria-label="Deselect all members"
              className="text-sm text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 rounded px-2 py-1 transition-all"
            >
              Deselect All
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="polite">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md" role="listbox" aria-label="Available team members">
          {loading ? (
            <div className="p-4 text-center" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" aria-hidden="true"></div>
              <p className="mt-2 text-sm text-gray-600">Loading members...</p>
              <span className="sr-only">Loading team members, please wait</span>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-4 text-center text-gray-500" role="status">
              <p>
                {searchTerm
                  ? 'No members match your search.'
                  : 'No members found.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredMembers.map((member) => {
                const isSelected = selectedMembers.some(m => m.id === member.id);
                return (
                  <div
                    key={member.id}
                    role="option"
                    aria-selected={isSelected}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    onClick={() => handleMemberSelection(member)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleMemberSelection(member);
                      }
                    }}
                    tabIndex={0}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleMemberSelection(member)}
                        aria-label={`Select ${member.firstname} ${member.lastname}`}
                        className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 mr-3"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {member.firstname} {member.lastname}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {member.login}
                            </p>
                            {(member.years_of_exp || member.skills) && (
                              <p className="mt-1 text-xs text-gray-600">
                                {member.years_of_exp ? `${member.years_of_exp} yrs exp` : ''}
                                {member.years_of_exp && member.skills ? ' · ' : ''}
                                {member.skills}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              ₹{(member.per_hour_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hr
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            aria-label="Cancel and close modal"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleAddMembers}
            disabled={selectedMembers.length === 0}
            aria-label={`Add ${selectedMembers.length} selected member${selectedMembers.length !== 1 ? 's' : ''} to project`}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all ${
              selectedMembers.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            Add {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;
